import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Chip,
  Stack,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Snackbar
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import EmailIcon from '@mui/icons-material/Email';
import HistoryIcon from '@mui/icons-material/History';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { supabase } from '../../../supabaseClient';

const reminderIntervals = [
  { value: 7, label: '7 days overdue' },
  { value: 14, label: '14 days overdue' },
  { value: 21, label: '21 days overdue' },
  { value: 30, label: '30 days overdue' },
  { value: 'custom', label: 'Custom' }
];

const emailTemplates = [
  {
    id: 'friendly',
    name: 'Friendly Reminder',
    subject: 'Payment Reminder',
    body: `Dear Customer,

This is a friendly reminder regarding your overdue invoice(s).

If you have already sent payment, please disregard this notice.

Thank you for your prompt attention to this matter.

Best regards`
  },
  {
    id: 'standard',
    name: 'Standard Reminder',
    subject: 'Payment Required',
    body: `Dear Customer,

We are writing to remind you about your overdue invoice(s).

Please arrange payment immediately to avoid any service interruptions.

If you have any questions or concerns, please contact us at your earliest convenience.

Thank you`
  },
  {
    id: 'urgent',
    name: 'Urgent Reminder',
    subject: 'URGENT: Payment Required',
    body: `Dear Customer,

This is an urgent notice regarding your overdue invoice(s).

Immediate payment is required. Failure to remit payment may result in:
- Account suspension
- Late fees
- Collection proceedings

Please contact us immediately to arrange payment or discuss payment options.`
  }
];

const AutoCollectionsDialog = ({ open, onClose, receivables, companyId, canEdit = true, canDelete = true }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [reminderInterval, setReminderInterval] = useState(7);
  const [customDays, setCustomDays] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('friendly');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sending, setSending] = useState(false);
  const [collectionHistory, setCollectionHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [companyName, setCompanyName] = useState('Your Company');

  // Filter overdue invoices
  const overdueInvoices = receivables.filter(r => 
    r.status.toLowerCase() === 'overdue' && r.daysOutstanding > 0
  );

  // Filter by reminder interval - shows ALL invoices <= the selected days
  const filteredInvoices = overdueInvoices.filter(inv => {
    let maxDays = 0;
    
    if (reminderInterval === 'custom') {
      const customDaysNum = parseInt(customDays);
      if (isNaN(customDaysNum) || customDaysNum < 0) return false;
      maxDays = customDaysNum;
    } else {
      maxDays = reminderInterval;
    }
    
    // Return all invoices that are <= maxDays overdue
    return inv.daysOutstanding <= maxDays;
  });

  useEffect(() => {
    if (open && activeTab === 1) {
      fetchCollectionHistory();
    }
  }, [open, activeTab]);

  const fetchCollectionHistory = async () => {
    setLoadingHistory(true);
    try {
      // In a real implementation, you'd fetch from a collection_history table
      // For now, we'll use mock data or create the table structure
      const { data, error } = await supabase
        .from('collection_history')
        .select('*')
        .eq('company_id', companyId)
        .order('sent_at', { ascending: false })
        .limit(50);

      if (error && error.code !== 'PGRST116') {
        // Table doesn't exist yet, use empty array
        setCollectionHistory([]);
      } else {
        setCollectionHistory(data || []);
      }
    } catch (err) {
      console.error('Error fetching collection history:', err);
      setCollectionHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedInvoices(filteredInvoices.map(inv => inv.id));
    } else {
      setSelectedInvoices([]);
    }
  };

  const handleSelectInvoice = (invoiceId, checked) => {
    if (checked) {
      setSelectedInvoices([...selectedInvoices, invoiceId]);
    } else {
      setSelectedInvoices(selectedInvoices.filter(id => id !== invoiceId));
    }
  };

  // Initialize and update email subject and body when template changes
  useEffect(() => {
    const template = emailTemplates.find(t => t.id === selectedTemplate) || emailTemplates[0];
    if (template) {
      setEmailSubject(template.subject);
      setEmailBody(template.body);
    }
  }, [selectedTemplate]);

  // Initialize on mount
  useEffect(() => {
    if (open && emailSubject === '' && emailBody === '') {
      const template = emailTemplates.find(t => t.id === selectedTemplate) || emailTemplates[0];
      if (template) {
        setEmailSubject(template.subject);
        setEmailBody(template.body);
      }
    }
  }, [open]);

  // Fetch company name
  useEffect(() => {
    const fetchCompanyName = async () => {
      if (companyId) {
        try {
          const { data, error } = await supabase
            .from('companies')
            .select('name')
            .eq('id', companyId)
            .single();
          
          if (!error && data) {
            setCompanyName(data.name);
          }
        } catch (err) {
          console.error('Error fetching company name:', err);
        }
      }
    };
    
    if (open) {
      fetchCompanyName();
    }
  }, [open, companyId]);

  const getTemplate = () => {
    return {
      subject: emailSubject || emailTemplates[0].subject,
      body: emailBody || emailTemplates[0].body
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const generateEmailContent = (templateSubject, templateBody, invoices, customerName) => {
    if (!invoices || invoices.length === 0) {
      return { subject: templateSubject, body: templateBody };
    }

    // Calculate totals
    const totalAmount = invoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);
    
    // Generate invoice list
    const invoiceList = invoices.map(inv => {
      const daysOverdue = inv.daysOutstanding || 0;
      const dueDate = formatDate(inv.dueDate);
      return `  • Invoice ${inv.documentNumber}: ${formatCurrency(inv.amount || 0)} (${daysOverdue} day${daysOverdue !== 1 ? 's' : ''} overdue, due ${dueDate})`;
    }).join('\n');

    // Build subject
    let subject = templateSubject;
    if (invoices.length === 1) {
      subject = `${templateSubject} - Invoice ${invoices[0].documentNumber}`;
    } else {
      subject = `${templateSubject} - ${invoices.length} Overdue Invoice${invoices.length > 1 ? 's' : ''}`;
    }

    // Build body with automatic invoice list
    let body = templateBody;
    
    // Replace "Customer" with actual customer name
    body = body.replace(/Dear Customer,/g, `Dear ${customerName},`);
    
    // Insert invoice details after the greeting
    const invoiceDetails = `\n\nOVERDUE INVOICE${invoices.length > 1 ? 'S' : ''}:\n\n${invoiceList}\n\nTotal Amount Due: ${formatCurrency(totalAmount)}`;
    
    // Find where to insert (after first paragraph or after greeting)
    const greetingIndex = body.indexOf('\n\n');
    if (greetingIndex > 0) {
      body = body.substring(0, greetingIndex) + invoiceDetails + body.substring(greetingIndex);
    } else {
      body = body + invoiceDetails;
    }
    
    // Add company name at the end
    body = body + `\n\n${companyName}`;

    return { subject, body };
  };

  const handleSendReminders = async () => {
    if (!canEdit) {
      setSnackbar({ 
        open: true, 
        message: 'You do not have permission to send collection reminders.', 
        severity: 'error' 
      });
      return;
    }

    if (selectedInvoices.length === 0) {
      setSnackbar({ open: true, message: 'Please select at least one invoice to send reminders for.', severity: 'warning' });
      return;
    }

    setSending(true);
    try {
      const template = getTemplate();
      const selectedInvoiceData = receivables.filter(r => selectedInvoices.includes(r.id));
      
      // Get company name
      const { data: company } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();

      const companyName = company?.name || 'Your Company';

      // Get customer emails - you'll need to fetch these from the invoices
      const invoiceIds = selectedInvoiceData.map(inv => inv.id);
      const { data: invoicesWithCustomers } = await supabase
        .from('invoices')
        .select(`
          id,
          invoice_number,
          total_amount,
          due_date,
          customer:customer_id(id, name, email)
        `)
        .in('id', invoiceIds);

      // Group invoices by customer
      const invoicesByCustomer = {};
      for (const invoice of selectedInvoiceData) {
        const invoiceData = invoicesWithCustomers?.find(inv => inv.id === invoice.id);
        const customerId = invoiceData?.customer?.id;
        const customerName = invoice.customer;
        
        if (!customerId) continue;
        
        if (!invoicesByCustomer[customerId]) {
          invoicesByCustomer[customerId] = {
            customerId: customerId,
            customerName: customerName,
            customerEmail: invoiceData?.customer?.email,
            invoices: []
          };
        }
        
        invoicesByCustomer[customerId].invoices.push(invoice);
      }

      // Send emails and track history - one email per customer
      const collectionRecords = [];
      
      for (const customerId in invoicesByCustomer) {
        const customerGroup = invoicesByCustomer[customerId];
        const customerEmail = customerGroup.customerEmail;
        
        if (!customerEmail) {
          console.warn(`No email found for customer ${customerGroup.customerName}`);
          continue;
        }

        // Generate email content automatically with invoice list
        const emailContent = generateEmailContent(template.subject, template.body, customerGroup.invoices, customerGroup.customerName);
        const subject = emailContent.subject;
        const body = emailContent.body;

        // Calculate reminder interval as integer (handle 'custom' case)
        let reminderIntervalValue = reminderInterval;
        if (reminderInterval === 'custom') {
          reminderIntervalValue = parseInt(customDays) || 0;
        }

        // Create one collection record per invoice (for tracking), but send one email per customer
        for (const invoice of customerGroup.invoices) {
          const invoiceData = invoicesWithCustomers?.find(inv => inv.id === invoice.id);
          
          const collectionRecord = {
            company_id: companyId,
            invoice_id: invoice.id,
            invoice_number: invoice.documentNumber || '',
            customer_id: customerGroup.customerId || null,
            customer_name: customerGroup.customerName || '',
            customer_email: customerEmail || '',
            reminder_type: selectedTemplate || 'custom',
            reminder_interval: parseInt(reminderIntervalValue) || 0,
            days_overdue: parseInt(invoice.daysOutstanding) || 0,
            subject: subject || '',
            email_body: body || '',
            sent_at: new Date().toISOString(),
            status: 'sent'
          };

          collectionRecords.push(collectionRecord);
        }

        // TODO: Actually send email via Supabase Edge Function
        // For now, we'll just log it
        console.log('Would send email:', {
          to: customerEmail,
          customer: customerGroup.customerName,
          invoiceCount: customerGroup.invoices.length,
          subject,
          body
        });
      }

      // Save collection history
      if (collectionRecords.length > 0) {
        const { error: insertError } = await supabase
          .from('collection_history')
          .insert(collectionRecords);

        if (insertError) {
          console.error('Error saving collection history:', insertError);
          if (insertError.code === '42P01') {
            setSnackbar({ 
              open: true, 
              message: 'Collection history table does not exist. Please create it in your database.', 
              severity: 'error' 
            });
          } else {
            setSnackbar({ 
              open: true, 
              message: `Error saving collection history: ${insertError.message}`, 
              severity: 'error' 
            });
          }
        }
      }

      setSnackbar({ 
        open: true, 
        message: `Reminders sent successfully to ${collectionRecords.length} customer(s).`, 
        severity: 'success' 
      });
      setSelectedInvoices([]);
      if (activeTab === 1) {
        fetchCollectionHistory();
      }
    } catch (err) {
      console.error('Error sending reminders:', err);
      setSnackbar({ 
        open: true, 
        message: 'Failed to send reminders. Please try again.', 
        severity: 'error' 
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 24px 48px ${alpha(theme.palette.grey[900], 0.15)}`
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1.5, 
        pt: 2.5,
        px: 3,
        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <EmailIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Auto-Collections
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            px: 3,
            '& .MuiTab-root': {
              fontWeight: 500,
              fontSize: '0.9rem',
              textTransform: 'none',
              minHeight: 48
            }
          }}
        >
          <Tab icon={<SendIcon />} iconPosition="start" label="Send Reminders" />
          <Tab icon={<HistoryIcon />} iconPosition="start" label="Collection History" />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            <Stack spacing={3}>
              {/* Reminder Interval Selection */}
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2 }}>
                  Select Overdue Period
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Days Overdue</InputLabel>
                  <Select
                    value={reminderInterval}
                    label="Days Overdue"
                    onChange={(e) => {
                      setReminderInterval(e.target.value);
                      setSelectedInvoices([]);
                      if (e.target.value !== 'custom') {
                        setCustomDays('');
                      }
                    }}
                  >
                    {reminderIntervals.map(interval => (
                      <MenuItem key={interval.value} value={interval.value}>
                        {interval.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {reminderInterval === 'custom' && (
                  <TextField
                    fullWidth
                    type="number"
                    label="Enter minimum days overdue"
                    value={customDays}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || (!isNaN(value) && parseInt(value) >= 0)) {
                        setCustomDays(value);
                        setSelectedInvoices([]);
                      }
                    }}
                    inputProps={{ min: 0 }}
                    helperText="Show all invoices that are at least this many days overdue"
                    sx={{ mb: 2 }}
                  />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {filteredInvoices.length} invoice(s) found {reminderInterval === 'custom' && customDays ? `(${customDays} days overdue or less)` : reminderInterval !== 'custom' ? `(${reminderInterval} days overdue or less)` : ''}
                </Typography>
              </Box>

              {/* Invoice Selection */}
              {filteredInvoices.length > 0 ? (
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      Select Invoices
                    </Typography>
                    <Chip 
                      label={`${selectedInvoices.length} selected`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Stack>
                  <Paper sx={{ border: `1px solid ${alpha(theme.palette.grey[300], 0.6)}` }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedInvoices.length === filteredInvoices.length && filteredInvoices.length > 0}
                              indeterminate={selectedInvoices.length > 0 && selectedInvoices.length < filteredInvoices.length}
                              onChange={(e) => handleSelectAll(e.target.checked)}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>Invoice</TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>Customer</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500 }}>Amount</TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>Due Date</TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>Days Overdue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredInvoices.map((invoice) => (
                          <TableRow key={invoice.id} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedInvoices.includes(invoice.id)}
                                onChange={(e) => handleSelectInvoice(invoice.id, e.target.checked)}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {invoice.documentNumber}
                              </Typography>
                            </TableCell>
                            <TableCell>{invoice.customer}</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatCurrency(invoice.amount)}
                              </Typography>
                            </TableCell>
                            <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                            <TableCell>
                              <Chip
                                label={`+${invoice.daysOutstanding} days`}
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </Box>
              ) : (
                <Alert severity="info">
                  No invoices found for the selected overdue period.
                </Alert>
              )}

              {/* Email Template Selection */}
              {selectedInvoices.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2 }}>
                    Email Template
                  </Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Template</InputLabel>
                    <Select
                      value={selectedTemplate}
                      label="Template"
                      onChange={(e) => {
                        setSelectedTemplate(e.target.value);
                      }}
                    >
                      {emailTemplates.map(template => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name}
                        </MenuItem>
                      ))}
                      <MenuItem value="custom">Custom Message</MenuItem>
                    </Select>
                  </FormControl>

                  {(() => {
                    // Group selected invoices by customer
                    const selectedInvoiceData = receivables.filter(r => selectedInvoices.includes(r.id));
                    const groupedByCustomer = {};
                    selectedInvoiceData.forEach(inv => {
                      const customer = inv.customer || 'Unknown Customer';
                      if (!groupedByCustomer[customer]) {
                        groupedByCustomer[customer] = [];
                      }
                      groupedByCustomer[customer].push(inv);
                    });
                    
                    // Get first customer group for display (or show all if multiple)
                    const firstCustomer = Object.keys(groupedByCustomer)[0];
                    const firstCustomerInvoices = groupedByCustomer[firstCustomer] || [];
                    const customerName = firstCustomer || 'Customer';
                    
                    // Generate compiled email content
                    const compiledEmail = selectedInvoices.length > 0 
                      ? generateEmailContent(emailSubject || '', emailBody || '', firstCustomerInvoices, customerName)
                      : { subject: emailSubject || '', body: emailBody || '' };
                    
                    return (
                      <Stack spacing={2}>
                        {Object.keys(groupedByCustomer).length > 1 && (
                          <Alert severity="info">
                            Showing compiled email for first customer ({firstCustomer}). Each customer will receive their own email with their invoices.
                          </Alert>
                        )}
                        <TextField
                          label="Email Subject"
                          fullWidth
                          value={compiledEmail.subject}
                          onChange={(e) => setEmailSubject(e.target.value)}
                          placeholder="Enter email subject..."
                        />
                        <TextField
                          label="Email Body"
                          fullWidth
                          multiline
                          rows={30}
                          value={compiledEmail.body}
                          onChange={(e) => setEmailBody(e.target.value)}
                          placeholder="Enter email body..."
                        />
                        <Alert severity="info">
                          <Typography variant="caption">
                            Invoice details (list, amounts, totals) are automatically added to your message. Customer name and company name are inserted automatically.
                          </Typography>
                        </Alert>
                      </Stack>
                    );
                  })()}
                </Box>
              )}
            </Stack>
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 2 }}>
              Collection Activity History
            </Typography>
            {loadingHistory ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : collectionHistory.length === 0 ? (
              <Alert severity="info">
                No collection history found. Reminders you send will appear here.
              </Alert>
            ) : (
              <Paper sx={{ border: `1px solid ${alpha(theme.palette.grey[300], 0.6)}` }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>Invoice</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>Customer</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>Template</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>Days Overdue</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {collectionHistory.map((record) => (
                      <TableRow key={record.id} hover>
                        <TableCell>{formatDate(record.sent_at)}</TableCell>
                        <TableCell>{record.invoice_number}</TableCell>
                        <TableCell>{record.customer_name}</TableCell>
                        <TableCell>
                          <Chip
                            label={record.reminder_type}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>+{record.days_overdue} days</TableCell>
                        <TableCell>
                          <Chip
                            label={record.status}
                            size="small"
                            color={record.status === 'sent' ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        p: 3, 
        borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
        gap: 2
      }}>
        <Button onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none' }}>
          Close
        </Button>
        {activeTab === 0 && (
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSendReminders}
            disabled={selectedInvoices.length === 0 || sending || !canEdit}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            {sending ? 'Sending...' : `Send Reminders (${selectedInvoices.length})`}
          </Button>
        )}
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AutoCollectionsDialog;

